import {
  CrmFieldService,
  StandardExternalIds,
  StandardIds,
} from '@galaxy/crm/services/field-service';
import { Injectable } from '@angular/core';
import {
  CRMCustomObjectTypeApiService,
  CrmObjectInterface,
  PhoneFieldsInterface,
} from '@vendasta/crm';
import {
  format,
  ParsedNumber,
  parseNumber,
} from 'libphonenumber-js';
import { ObjectType } from '../tokens-and-interfaces';
import {
  Observable,
  of,
} from 'rxjs';
import {
  map,
  switchMap,
} from 'rxjs/operators';

export type ObjectNames = {
  singularObjectName: string;
  pluralObjectName: string;
};

@Injectable({ providedIn: 'root' })
export class CrmObjectDisplayService {
  constructor(
    protected readonly crmFieldService: CrmFieldService,
    private readonly customObjectTypeService: CRMCustomObjectTypeApiService,
  ) {}

  getObjectDisplayName(objectType: ObjectType, crmObject: CrmObjectInterface | undefined): string {
    if (!crmObject) {
      return '';
    }
    switch (objectType) {
      case 'Contact':
        return this.contactDisplayName(crmObject);
      case 'Company':
        return this.companyDisplayName(crmObject);
      case 'Opportunity':
        return this.opportunityDisplayName(crmObject);
      case 'CustomObject':
        return this.customObjectDisplayName(crmObject);
      default:
        return crmObject.crmObjectId ?? '';
    }
  }

  getObjectNames(namespace$: Observable<string>, subtype?: string): Observable<ObjectNames> {
    if (!subtype) {
      return of({ singularObjectName: '', pluralObjectName: '' });
    }

    return namespace$.pipe(
      switchMap((namespace) => {
        return this.customObjectTypeService.getMultiCustomObjectType({
          namespace: namespace,
          customObjectTypeIds: [subtype],
        });
      }),
      map((resp) => {
        const customObjectTypeData = resp.customObjectTypes[0];
        if (!customObjectTypeData) {
          console.error(`Custom object type ${subtype} not found`);
          return { singularObjectName: '', pluralObjectName: '' };
        }
        return {
          singularObjectName: customObjectTypeData.singularObjectName,
          pluralObjectName: customObjectTypeData.pluralObjectName,
        };
      }),
    );
  }

  getAssociationInfo(objectType: ObjectType, crmObject: CrmObjectInterface): string {
    switch (objectType) {
      case 'Contact':
        return this.contactAssociationInfo(crmObject);
      default:
        return '';
    }
  }

  private contactAssociationInfo(crmObject: CrmObjectInterface): string {
    const email = this.crmFieldService.getFieldId(StandardExternalIds.Email);
    const emailData = crmObject.fields?.find((f) => f.fieldId === email);
    return emailData?.stringValue || '';
  }

  companyDisplayName(crmObject: CrmObjectInterface): string {
    const name = this.crmFieldService.getFieldId(StandardIds.CompanyName);
    const nameData = crmObject.fields?.find((f) => f.fieldId === name);
    return nameData?.stringValue || '';
  }

  private opportunityDisplayName(crmObject: CrmObjectInterface): string {
    const nameField = StandardIds.OpportunityName;
    const nameData = crmObject.fields?.find((f) => f.fieldId === nameField);
    return nameData?.stringValue || '';
  }

  contactDisplayName(crmObject: CrmObjectInterface): string {
    const firstName = this.crmFieldService.getFieldId(StandardExternalIds.FirstName);
    const lastName = this.crmFieldService.getFieldId(StandardExternalIds.LastName);
    const email = this.crmFieldService.getFieldId(StandardExternalIds.Email);
    const phone = this.crmFieldService.getFieldId(StandardExternalIds.PhoneNumber);

    const firstNameData = crmObject.fields?.find((f) => f.fieldId === firstName);
    const lastNameData = crmObject.fields?.find((f) => f.fieldId === lastName);
    const emailData = crmObject.fields?.find((f) => f.fieldId === email);
    const phoneData = crmObject.fields?.find((f) => f.fieldId === phone);

    if (firstNameData?.stringValue || lastNameData?.stringValue) {
      return `${firstNameData?.stringValue ?? ''} ${lastNameData?.stringValue ?? ''}`.trim();
    } else {
      if (emailData?.stringValue) {
        return emailData.stringValue || '';
      } else {
        if (phoneData?.phoneFieldValues) {
          return this.formatPhoneNumberFields(phoneData?.phoneFieldValues) || '';
        } else {
          return this.formatPhoneNumber(phoneData?.stringValue || '') || '';
        }
      }
    }
  }

  customObjectDisplayName(crmObject: CrmObjectInterface): string {
    const name = this.crmFieldService.getFieldId(StandardIds.CustomObjectName);
    const nameData = crmObject.fields?.find((f) => f.fieldId === name);
    return nameData?.stringValue || '';
  }

  private formatPhoneNumber(phoneNumber: string): string {
    const parsedNumber = parseNumber(phoneNumber, {
      defaultCountry: 'US',
    });
    if (Object.keys(parsedNumber).length === 0) {
      return '';
    }

    return format(parsedNumber as ParsedNumber, 'INTERNATIONAL');
  }

  private formatPhoneNumberFields(phoneNumber: PhoneFieldsInterface): string {
    if (phoneNumber.nationalNumber === '') {
      return '';
    }
    const parsed = {
      country: phoneNumber.isoCountryCode,
      phone: phoneNumber.nationalNumber,
      ext: phoneNumber.extension,
    } as ParsedNumber;
    return format(parsed, 'INTERNATIONAL');
  }
}
